import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone, MagnifyingGlass, Plus, PencilSimple, Trash, X,
  Warning, Info, WarningCircle, CheckCircle, PauseCircle, PlayCircle,
  ArrowsClockwise, Funnel,
} from "@phosphor-icons/react";

const SEVERITIES = [
  { v: "info",     l: "Info",     bg: "bg-blue-50",  fg: "text-blue-700",     dot: "#3B82F6", icon: Info },
  { v: "warning",  l: "Warning",  bg: "bg-amber-50", fg: "text-amber-700",    dot: "#F59E0B", icon: Warning },
  { v: "critical", l: "Critical", bg: "bg-red-50",   fg: "text-red-700",      dot: "#EF4444", icon: WarningCircle },
];
const TYPES = ["diversion", "delay", "cancellation", "advisory", "weather", "incident"];

const sevMeta = (v) => SEVERITIES.find((s) => s.v === v) || SEVERITIES[0];

export default function ServiceAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, live_now: 0, by_severity: {}, by_type: {} });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  // Filters
  const [q, setQ] = useState("");
  const [severity, setSeverity] = useState("All");
  const [type, setType] = useState("All");
  const [activeFlag, setActiveFlag] = useState("All");   // All | Active | Paused

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (severity !== "All") params.set("severity", severity);
      if (type !== "All") params.set("type", type);
      if (activeFlag === "Active") params.set("active", "true");
      if (activeFlag === "Paused") params.set("active", "false");
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get(`/admin/service-alerts${params.toString() ? `?${params}` : ""}`),
        api.get("/admin/service-alerts/stats"),
      ]);
      setAlerts(list || []);
      setStats(s);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line
  useEffect(() => { const t = setTimeout(refresh, 250); return () => clearTimeout(t); }, [q, severity, type, activeFlag]); // eslint-disable-line

  const onSaved = (saved) => {
    setShowForm(false); setEditing(null);
    refresh();
    toast.success(saved?.id ? "Alert saved" : "Alert created");
  };

  const toggle = async (a) => {
    try {
      const { data } = await api.patch(`/admin/service-alerts/${a.id}/toggle`);
      setAlerts((p) => p.map((x) => x.id === a.id ? { ...x, is_active: data.is_active } : x));
      toast.success(data.is_active ? "Alert activated" : "Alert paused");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete alert "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/service-alerts/${a.id}`);
      setAlerts((p) => p.filter((x) => x.id !== a.id));
      toast.success("Alert deleted");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const KPIS = useMemo(() => [
    { l: "Total Alerts",  v: stats.total,    c: "#1E3A8A", i: Megaphone, testid: "sa-kpi-total" },
    { l: "Live Now",      v: stats.live_now, c: "#22C55E", i: CheckCircle, testid: "sa-kpi-live" },
    { l: "Critical",      v: stats.by_severity?.critical || 0, c: "#EF4444", i: WarningCircle, testid: "sa-kpi-critical" },
    { l: "Warnings",      v: stats.by_severity?.warning  || 0, c: "#F59E0B", i: Warning, testid: "sa-kpi-warn" },
  ], [stats]);

  return (
    <div className="p-4 lg:p-6 space-y-5 lg:space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-slate-500">Public Communications</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">Service Alerts & Route Diversions</h1>
          <p className="text-[12px] text-slate-500 mt-1">
            Publish alerts that appear in the MajheApp customer home screen and the dedicated Alerts page.
            Critical alerts surface as a banner; warnings + info appear in the list.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={refresh} data-testid="sa-refresh"
            className="px-3 py-2 rounded-md text-[12px] font-bold bg-slate-100 hover:bg-slate-200 inline-flex items-center gap-1.5">
            <ArrowsClockwise size={13} weight="bold"/> Refresh
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="sa-add-btn"
            className="ktcl-btn-primary px-4 py-2 rounded-md text-sm font-bold inline-flex items-center gap-1.5">
            <Plus size={14}/> New Alert
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sa-kpis">
        {KPIS.map((k) => (
          <div key={k.l} className="ktcl-card ktcl-card-hover p-4" data-testid={k.testid}>
            <div className="flex items-center justify-between">
              <div className="label-cap text-slate-500">{k.l}</div>
              <k.i size={18} weight="duotone" style={{ color: k.c }}/>
            </div>
            <div className="font-display text-2xl font-bold mt-2 tabular-nums" style={{ color: k.c }}>
              {(k.v || 0).toLocaleString("en-IN")}
            </div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="ktcl-card p-3 lg:p-4 flex flex-wrap items-center gap-2 lg:gap-3" data-testid="sa-filters">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-md w-full sm:w-64">
          <MagnifyingGlass size={14} className="text-slate-500"/>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search title or message…"
            data-testid="sa-search"
            className="bg-transparent text-sm outline-none flex-1 min-w-0"/>
        </div>
        <Sel value={severity} setter={setSeverity} label="Severity"
              options={["All", ...SEVERITIES.map(s => s.v)]} testid="sa-filter-sev"/>
        <Sel value={type} setter={setType} label="Type"
              options={["All", ...TYPES]} testid="sa-filter-type"/>
        <Sel value={activeFlag} setter={setActiveFlag} label="Status"
              options={["All", "Active", "Paused"]} testid="sa-filter-active"/>
        <div className="ml-auto text-xs text-slate-500" data-testid="sa-count">
          <Funnel size={11} weight="bold" className="inline mr-1"/>
          {loading ? "Loading…" : `${alerts.length} alert${alerts.length !== 1 ? "s" : ""}`}
        </div>
      </div>

      {/* List */}
      <div className="space-y-2" data-testid="sa-list">
        {alerts.map((a) => <AlertRow key={a.id} a={a} onEdit={() => { setEditing(a); setShowForm(true); }}
                                       onToggle={() => toggle(a)} onDelete={() => remove(a)}/>)}
        {alerts.length === 0 && !loading && (
          <div className="ktcl-card p-12 text-center" data-testid="sa-empty">
            <Megaphone size={36} weight="duotone" className="text-slate-300 mx-auto"/>
            <div className="mt-2 text-sm text-slate-500">No service alerts yet.</div>
            <button onClick={() => setShowForm(true)} className="mt-3 ktcl-btn-primary px-4 py-2 rounded-md text-sm font-bold inline-flex items-center gap-1.5">
              <Plus size={14}/> Create your first alert
            </button>
          </div>
        )}
      </div>

      {showForm && <AlertForm initial={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={onSaved}/>}
    </div>
  );
}

// ---------------------------------------------------------------------------
function AlertRow({ a, onEdit, onToggle, onDelete }) {
  const sev = sevMeta(a.severity);
  const Icon = sev.icon;
  const isPaused = !a.is_active;
  const isLive = (() => {
    if (!a.is_active) return false;
    const now = Date.now();
    if (a.starts_at && new Date(a.starts_at).getTime() > now) return false;
    if (a.ends_at   && new Date(a.ends_at).getTime() <= now) return false;
    return true;
  })();

  return (
    <div className={`ktcl-card overflow-hidden ${isPaused ? "opacity-60" : ""}`}
         data-testid={`sa-row-${a.id}`}>
      <div className={`px-4 py-3 ${sev.bg} flex items-center gap-3 border-b border-slate-200`}>
        <Icon size={18} weight="fill" style={{ color: sev.dot }}/>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-wider font-bold ${sev.fg}`}>
            {a.severity} · {a.type}
            {isLive && <span className="ml-2 bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">LIVE</span>}
            {isPaused && <span className="ml-2 bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">PAUSED</span>}
          </div>
          <div className="font-bold truncate">{a.title}</div>
        </div>
        <button onClick={onToggle} title={a.is_active ? "Pause" : "Activate"}
          data-testid={`sa-toggle-${a.id}`}
          className="text-[11px] px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-1">
          {a.is_active ? <><PauseCircle size={12}/> Pause</> : <><PlayCircle size={12}/> Activate</>}
        </button>
        <button onClick={onEdit} data-testid={`sa-edit-${a.id}`}
          className="text-[11px] px-2 py-1 rounded border border-[#1E3A8A]/30 bg-blue-50 text-[#1E3A8A] hover:bg-blue-100 font-bold inline-flex items-center gap-1">
          <PencilSimple size={12}/> Edit
        </button>
        <button onClick={onDelete} data-testid={`sa-delete-${a.id}`}
          className="text-[11px] px-2 py-1 rounded border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 inline-flex items-center gap-1">
          <Trash size={12}/>
        </button>
      </div>
      {a.message && <div className="px-4 py-2 text-[13px] text-slate-700 whitespace-pre-wrap">{a.message}</div>}
      <div className="px-4 py-2 text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-1 border-t border-slate-100 bg-slate-50">
        {a.affected_routes?.length > 0 && (
          <span>Routes: {a.affected_routes.map((r) => (
            <code key={r} className="font-mono bg-white border border-slate-200 px-1 rounded mr-1">{r}</code>
          ))}</span>
        )}
        {a.affected_stop_ids?.length > 0 && (
          <span>Stops: {a.affected_stop_ids.slice(0, 5).map((s) => <code key={s} className="font-mono bg-white border border-slate-200 px-1 rounded mr-1">#{s}</code>)}
            {a.affected_stop_ids.length > 5 && <span className="text-slate-400">+{a.affected_stop_ids.length - 5} more</span>}
          </span>
        )}
        {a.alternative && <span>Alternative: <i>{a.alternative}</i></span>}
        {a.starts_at && <span>Starts: {new Date(a.starts_at).toLocaleString("en-IN")}</span>}
        {a.ends_at && <span>Ends: {new Date(a.ends_at).toLocaleString("en-IN")}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function AlertForm({ initial, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    title:        initial?.title || "",
    message:      initial?.message || "",
    severity:     initial?.severity || "info",
    type:         initial?.type || "advisory",
    affected_routes:   (initial?.affected_routes || []).join(", "),
    affected_stop_ids: (initial?.affected_stop_ids || []).join(", "),
    alternative:  initial?.alternative || "",
    starts_at:    initial?.starts_at ? toLocalInput(initial.starts_at) : "",
    ends_at:      initial?.ends_at   ? toLocalInput(initial.ends_at)   : "",
    is_active:    initial?.is_active !== false,
  }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !saving) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saving, onClose]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = {
      title:    form.title.trim(),
      message:  form.message.trim(),
      severity: form.severity,
      type:     form.type,
      affected_routes:   form.affected_routes.split(",").map((s) => s.trim()).filter(Boolean),
      affected_stop_ids: form.affected_stop_ids.split(",").map((s) => Number(s.trim())).filter((n) => Number.isFinite(n)),
      alternative: form.alternative.trim(),
      starts_at:   form.starts_at ? new Date(form.starts_at).toISOString() : null,
      ends_at:     form.ends_at   ? new Date(form.ends_at).toISOString()   : null,
      is_active:   !!form.is_active,
    };
    try {
      const url = initial?.id ? `/admin/service-alerts/${initial.id}` : "/admin/service-alerts";
      const method = initial?.id ? "put" : "post";
      const { data } = await api[method](url, payload);
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[1400] flex items-center justify-center p-4"
         data-testid="sa-form-dialog"
         onClick={() => { if (!saving) onClose(); }}>
      <div className="bg-white rounded-md max-w-2xl w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <Megaphone size={18} weight="duotone" className="text-[#1E3A8A]"/>
            {initial?.id ? "Edit Service Alert" : "Create Service Alert"}
          </h2>
          <button onClick={onClose} disabled={saving}
            className="w-8 h-8 rounded-md bg-slate-100 text-slate-500 inline-flex items-center justify-center disabled:opacity-40"
            aria-label="Close"><X size={16}/></button>
        </div>

        <Fld label="Title *">
          <In v={form.title} on={(v) => set("title", v)} testid="sa-form-title"
              ph="e.g. Margao-Panaji route diverted via NH66"/>
        </Fld>

        <Fld label="Message (shown to passengers)">
          <textarea value={form.message} onChange={(e) => set("message", e.target.value)}
            data-testid="sa-form-message" rows={3}
            placeholder="Brief description of the diversion, expected impact, and any guidance."
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
        </Fld>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Fld label="Severity *">
            <select value={form.severity} onChange={(e) => set("severity", e.target.value)}
              data-testid="sa-form-severity"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]">
              {SEVERITIES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </Fld>
          <Fld label="Type *">
            <select value={form.type} onChange={(e) => set("type", e.target.value)}
              data-testid="sa-form-type"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Fld>
        </div>

        <Fld label="Affected route codes (comma-separated, e.g. PNJ10, MRG1)">
          <In v={form.affected_routes} on={(v) => set("affected_routes", v)}
              testid="sa-form-routes" ph="PNJ10, MRG1"/>
        </Fld>

        <Fld label="Affected stop IDs (comma-separated numbers, optional)">
          <In v={form.affected_stop_ids} on={(v) => set("affected_stop_ids", v)}
              testid="sa-form-stops" ph="196, 1, 524"/>
        </Fld>

        <Fld label="Alternative / workaround text (optional)">
          <In v={form.alternative} on={(v) => set("alternative", v)}
              testid="sa-form-alternative" ph="e.g. Allow extra 20 minutes, or use route XYZ"/>
        </Fld>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Fld label="Starts (optional — defaults to immediately)">
            <input type="datetime-local" value={form.starts_at}
              onChange={(e) => set("starts_at", e.target.value)}
              data-testid="sa-form-starts"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
          </Fld>
          <Fld label="Ends (optional — leave blank for indefinite)">
            <input type="datetime-local" value={form.ends_at}
              onChange={(e) => set("ends_at", e.target.value)}
              data-testid="sa-form-ends"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
          </Fld>
        </div>

        <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="sa-form-active-label">
          <input type="checkbox" checked={form.is_active}
            onChange={(e) => set("is_active", e.target.checked)}
            data-testid="sa-form-active" className="w-4 h-4 accent-[#1E3A8A]"/>
          Publish alert (visible to passengers when within time window)
        </label>

        <div className="flex gap-2 pt-2 sticky bottom-0 bg-white">
          <button onClick={onClose} disabled={saving}
            data-testid="sa-form-cancel"
            className="flex-1 px-3 py-2 rounded-md text-sm border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            data-testid="sa-form-save"
            className="ktcl-btn-primary flex-1 px-3 py-2 rounded-md text-sm font-bold disabled:opacity-50">
            {saving ? "Saving…" : initial?.id ? "Save changes" : "Publish alert"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- helpers --------------------------------------------------------------
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

const Fld = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{label}</div>
    {children}
  </div>
);
const In = ({ v, on, testid, ph }) => (
  <input value={v} onChange={(e) => on(e.target.value)}
    data-testid={testid} placeholder={ph}
    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
);
const Sel = ({ value, setter, options, label, testid }) => (
  <label className="flex items-center gap-1.5">
    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</span>
    <select value={value} onChange={(e) => setter(e.target.value)}
      data-testid={testid}
      className="px-2.5 py-1.5 bg-slate-100 rounded-md text-[12px] outline-none border border-transparent focus:border-[#1E3A8A] min-w-[100px]">
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  </label>
);
